<script setup lang="ts">
import { computed, nextTick, onMounted, ref, shallowRef, watch } from 'vue';
import { CoreTypes, type Id, type ObjectRecord } from '@vp/shared-types';
import { compareZ } from '@vp/core';
import { live, ui } from '../editor/bridge';
import { t, typeLabel } from '../i18n';
import { moduleTypeLabel } from '../modules';
import Icon from './Icon.vue';

/**
 * Object tree (§6.9): names, types, groups and layer order; keyboard navigable,
 * virtualized for tens of thousands of objects; also the screen-reader path to
 * canvas content (§16.2).
 */
interface Row {
  id: Id;
  depth: number;
  label: string;
  /** Type name shown under/after the label when the label is the object's text. */
  sub: string;
  icon: string;
  type: string;
  isGroup: boolean;
  locked: boolean;
  setSize: number;
  pos: number;
}

const ROW_H = 32;
/** The list grows with the canvas up to this many rows, then scrolls. */
const MAX_ROWS = 20;
const scroller = ref<HTMLDivElement | null>(null);
const scrollTop = ref(0);
const viewH = ref(400);
const expanded = ref(new Set<Id>());
const filter = ref('');
const rows = shallowRef<Row[]>([]);
const focusIdx = ref(0);
let rebuildTimer: ReturnType<typeof setTimeout> | null = null;

function labelOf(o: ObjectRecord): { label: string; sub: string } {
  const base = typeLabel(o.type, moduleTypeLabel);
  if (o.name) return { label: o.name, sub: base };
  const text = (o.props['text'] ?? o.props['title'] ?? o.props['label']) as string | undefined;
  return text && typeof text === 'string' && text.trim() ? { label: text.trim().replace(/\s+/g, ' ').slice(0, 60), sub: base } : { label: base, sub: '' };
}

function iconOf(type: string): string {
  return type.startsWith('core:') ? type.slice(5) : 'module';
}

function rebuild(): void {
  const ed = live.editor;
  if (!ed) {
    rows.value = [];
    return;
  }
  const doc = ed.doc;
  const q = filter.value.trim().toLowerCase();
  const out: Row[] = [];
  const childrenSorted = (ids: Id[]) =>
    ids
      .map((id) => doc.get(id)!)
      .filter(Boolean)
      .sort((a, b) => compareZ(b, a));
  const roots: ObjectRecord[] = [];
  for (const o of doc.allObjects()) {
    if (o.parentId && doc.has(o.parentId)) continue;
    if (o.frameId && doc.has(o.frameId)) continue;
    roots.push(o);
  }
  roots.sort((a, b) => compareZ(b, a));
  const walk = (list: ObjectRecord[], depth: number) => {
    list.forEach((o, i) => {
      const { label, sub } = labelOf(o);
      // groups and frames are containers
      const kids = [...doc.childrenOf(o.id), ...doc.membersOf(o.id)];
      const isGroup = o.type === CoreTypes.group || o.type === CoreTypes.frame || kids.length > 0;
      if (!q || label.toLowerCase().includes(q) || sub.toLowerCase().includes(q)) {
        out.push({ id: o.id, depth, label, sub, icon: iconOf(o.type), type: o.type, isGroup, locked: o.locked, setSize: list.length, pos: i + 1 });
      }
      if (isGroup && kids.length && (expanded.value.has(o.id) || q)) walk(childrenSorted(kids), depth + 1);
    });
  };
  walk(roots, 0);
  rows.value = out;
}

function scheduleRebuild(): void {
  if (rebuildTimer) return;
  rebuildTimer = setTimeout(() => {
    rebuildTimer = null;
    rebuild();
  }, 150);
}

watch(() => ui.docRev, scheduleRebuild);
watch(filter, rebuild);
onMounted(() => {
  rebuild();
  viewH.value = scroller.value?.clientHeight ?? 400;
});

const total = computed(() => {
  void ui.docRev;
  return live.editor?.doc.size ?? 0;
});
/** Height of the list: grows with the rows, capped at MAX_ROWS. */
const listH = computed(() => Math.min(rows.value.length, MAX_ROWS) * ROW_H + 8);
watch(listH, (h) => (viewH.value = h), { immediate: true });

function toggleLock(row: Row): void {
  const ed = live.editor;
  if (!ed) return;
  ed.select([row.id]);
  ed.toggleLock();
}

const start = computed(() => Math.max(0, Math.floor(scrollTop.value / ROW_H) - 5));
const end = computed(() => Math.min(rows.value.length, Math.ceil((scrollTop.value + viewH.value) / ROW_H) + 5));
const visibleRows = computed(() => rows.value.slice(start.value, end.value));
const selected = computed(() => new Set(ui.selection));

function onScroll(): void {
  scrollTop.value = scroller.value?.scrollTop ?? 0;
  viewH.value = scroller.value?.clientHeight ?? 400;
}

function select(row: Row, e?: MouseEvent | KeyboardEvent): void {
  const ed = live.editor;
  if (!ed) return;
  if (e && (e.ctrlKey || e.metaKey || e.shiftKey)) ed.select([row.id], 'toggle');
  else ed.select([row.id]);
}

function reveal(row: Row): void {
  const ed = live.editor;
  if (!ed) return;
  ed.select([row.id]);
  ed.fitSelection();
}

function toggle(row: Row): void {
  const s = new Set(expanded.value);
  if (s.has(row.id)) s.delete(row.id);
  else s.add(row.id);
  expanded.value = s;
  rebuild();
}

function focusRow(i: number): void {
  focusIdx.value = Math.max(0, Math.min(rows.value.length - 1, i));
  const el = scroller.value;
  if (!el) return;
  const top = focusIdx.value * ROW_H;
  if (top < el.scrollTop) el.scrollTop = top;
  else if (top + ROW_H > el.scrollTop + el.clientHeight) el.scrollTop = top + ROW_H - el.clientHeight;
  void nextTick(() => (el.querySelector(`[data-idx="${focusIdx.value}"]`) as HTMLElement | null)?.focus());
}

function onKey(e: KeyboardEvent): void {
  const row = rows.value[focusIdx.value];
  switch (e.key) {
    case 'ArrowDown':
      focusRow(focusIdx.value + 1);
      if (!e.ctrlKey) select(rows.value[focusIdx.value]);
      break;
    case 'ArrowUp':
      focusRow(focusIdx.value - 1);
      if (!e.ctrlKey) select(rows.value[focusIdx.value]);
      break;
    case 'Home':
      focusRow(0);
      break;
    case 'End':
      focusRow(rows.value.length - 1);
      break;
    case 'ArrowRight':
      if (row?.isGroup && !expanded.value.has(row.id)) toggle(row);
      break;
    case 'ArrowLeft':
      if (row?.isGroup && expanded.value.has(row.id)) toggle(row);
      break;
    case 'Enter':
      if (row) reveal(row);
      break;
    case ' ':
      if (row) select(row, e);
      break;
    case 'Delete':
      live.editor?.deleteSelection();
      break;
    default:
      return;
  }
  e.preventDefault();
}

// keep keyboard focus index in sync with canvas selection
watch(
  () => ui.selection,
  (sel) => {
    if (sel.length !== 1) return;
    const i = rows.value.findIndex((r) => r.id === sel[0]);
    if (i >= 0) focusIdx.value = i;
  },
);
</script>

<template>
  <div class="tree-wrap">
    <div class="filter">
      <Icon name="search" :size="14" />
      <input v-model="filter" type="search" :placeholder="t('tree.search')" :aria-label="t('tree.search')" />
      <span class="count" :title="t('tree.title')">{{ filter ? rows.length + ' / ' + total : total }}</span>
    </div>
    <p v-if="!rows.length" class="empty">{{ filter ? t('tree.nothingFound') : t('tree.empty') }}</p>
    <div
      v-show="rows.length"
      ref="scroller"
      class="scroller"
      role="tree"
      :aria-label="t('tree.title')"
      :aria-multiselectable="true"
      :style="{ height: listH + 'px' }"
      @scroll="onScroll"
      @keydown="onKey"
    >
      <div :style="{ height: rows.length * ROW_H + 'px', position: 'relative' }">
        <div
          v-for="(row, i) in visibleRows"
          :key="row.id"
          class="row"
          role="treeitem"
          :data-idx="start + i"
          :tabindex="start + i === focusIdx ? 0 : -1"
          :aria-selected="selected.has(row.id)"
          :aria-level="row.depth + 1"
          :aria-setsize="row.setSize"
          :aria-posinset="row.pos"
          :aria-expanded="row.isGroup ? expanded.has(row.id) : undefined"
          :class="{ sel: selected.has(row.id), locked: row.locked }"
          :style="{ top: (start + i) * ROW_H + 'px', paddingLeft: 6 + row.depth * 18 + 'px' }"
          :title="row.sub ? row.label + ' · ' + row.sub : row.label"
          @click="(focusIdx = start + i), select(row, $event)"
          @dblclick="reveal(row)"
        >
          <button v-if="row.isGroup" class="twisty" tabindex="-1" :aria-hidden="true" @click.stop="toggle(row)">
            <Icon :name="expanded.has(row.id) ? 'chevronDown' : 'chevron'" :size="12" />
          </button>
          <span v-else class="twisty"></span>
          <span class="type-ico"><Icon :name="row.icon" :size="14" /></span>
          <span class="label">{{ row.label }}</span>
          <span v-if="row.sub" class="sub">{{ row.sub }}</span>
          <button class="lock" :class="{ on: row.locked }" tabindex="-1" :aria-label="row.locked ? t('cmd.unlock') : t('cmd.lock')" :title="row.locked ? t('cmd.unlock') : t('cmd.lock')" @click.stop="toggleLock(row)">
            <Icon :name="row.locked ? 'lock' : 'unlock'" :size="12" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tree-wrap {
  display: flex;
  flex-direction: column;
  flex: 0 1 auto;
  min-height: 0;
}
.filter {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 10px 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  background: var(--hover);
  color: var(--text-3);
}
.filter input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: none;
  font-size: 12.5px;
}
.count {
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  color: var(--text-3);
}
.scroller {
  /* explicit height from the row count; may still shrink when the window is short */
  flex: 0 1 auto;
  min-height: 0;
  overflow-y: auto;
  outline: none;
  padding: 0 6px 8px;
}
.row {
  position: absolute;
  left: 0;
  right: 0;
  height: 30px;
  margin-top: 1px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding-right: 6px;
  border-radius: 10px;
  cursor: pointer;
  white-space: nowrap;
  font-size: 12.5px;
  color: var(--text);
}
.row:hover {
  background: var(--panel-2);
}
.row.sel {
  background: var(--accent-soft);
  color: var(--accent-text);
}
.twisty {
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  padding: 0;
  border-radius: 6px;
  color: var(--text-3);
  flex: none;
  cursor: pointer;
}
.twisty:hover {
  background: var(--border);
}
.type-ico {
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  background: var(--hover);
  color: var(--text-2);
  flex: none;
}
.row.sel .type-ico {
  background: var(--panel);
  color: var(--accent-text);
}
.label {
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.sub {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 11px;
  color: var(--text-3);
}
.label:last-of-type {
  flex: 1;
}
.lock {
  margin-left: auto;
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  border-radius: 7px;
  color: var(--text-3);
  cursor: pointer;
  flex: none;
  opacity: 0;
}
.row:hover .lock,
.lock.on {
  opacity: 1;
}
.lock:hover {
  background: var(--border);
  color: var(--text);
}
.row.locked .label {
  color: var(--text-2);
}
.empty {
  margin: 4px 16px 14px;
  font-size: 12px;
  color: var(--text-3);
}
</style>
